import React, { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Calendar, CreditCard, ArrowRight, Settings, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface SubscriptionActiveData {
  planName: string;
  planPrice: number;
  planPeriod: string;
  nextBillingDate: string;
  status: "active" | "warning" | "pending";
  paymentMethod?: string;
  lastPaymentDate?: string;
  cancelUrl?: string;
}

interface SubscriptionActiveScreenProps {
  subscriptionData: SubscriptionActiveData;
  onUpgradePlanClick: () => void;
  onChangePaymentClick: () => void;
  userName?: string;
}

export function SubscriptionActiveScreen({
  subscriptionData,
  onUpgradePlanClick,
  onChangePaymentClick,
  userName = "usuário"
}: SubscriptionActiveScreenProps) {
  const navigate = useNavigate();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const getPaymentMethodIcon = (method?: string) => {
    // ✅ CORRIGIDO: Normalizar o método de pagamento
    // Valores possíveis segundo API ASAAS: UNDEFINED | BOLETO | CREDIT_CARD | DEBIT_CARD | TRANSFER | DEPOSIT | PIX
    if (!method) return "💳 Cartão";
    
    const normalized = method.trim().toUpperCase();
    
    console.log('🔍 getPaymentMethodIcon - Método recebido:', { original: method, normalized });
    
    switch (normalized) {
      case "PIX":
        return "💚 PIX";
      case "CREDIT_CARD":
      case "CREDITCARD":
        return "💳 Cartão";
      case "BOLETO":
        return "📄 Boleto";
      case "DEBIT_CARD":
      case "DEBITCARD":
        return "🏦 Débito";
      case "TRANSFER":
      case "TED":
        return "🔄 Transferência";
      case "DEPOSIT":
        return "💰 Depósito";
      case "UNDEFINED":
      case null:
      case "":
        return "💳 Cartão";
      default:
        console.warn('⚠️ Método de pagamento desconhecido:', method);
        return "💳 Cartão";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-50 border-green-200";
      case "warning":
        return "bg-yellow-50 border-yellow-200";
      case "pending":
        return "bg-blue-50 border-blue-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return { text: "Ativo", color: "bg-green-100 text-green-800" };
      case "warning":
        return { text: "Atenção Necessária", color: "bg-yellow-100 text-yellow-800" };
      case "pending":
        return { text: "Pendente", color: "bg-blue-100 text-blue-800" };
      default:
        return { text: "Desconhecido", color: "bg-gray-100 text-gray-800" };
    }
  };

  const statusInfo = getStatusText(subscriptionData.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div className="absolute -top-1 -right-1 bg-green-600 text-white rounded-full p-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Sua Assinatura Está Ativa</h1>
          <p className="text-lg text-gray-600">
            Bem-vindo, {userName}! Aproveite todos os recursos do {subscriptionData.planName}
          </p>
        </div>

        {/* Status Card */}
        <Card className={`border-2 ${getStatusColor(subscriptionData.status)}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-gray-900">Status da Assinatura</CardTitle>
              <Badge className={statusInfo.color}>{statusInfo.text}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Próxima cobrança */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
              <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Próxima Cobrança</div>
                <div className="text-sm text-gray-600">{subscriptionData.nextBillingDate}</div>
                <div className="text-xs text-gray-500 mt-1">
                  R$ {subscriptionData.planPrice.toFixed(2).replace('.', ',')} por {subscriptionData.planPeriod}
                </div>
              </div>
            </div>

            {/* Forma de pagamento */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
              <CreditCard className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Forma de Pagamento</div>
                <div className="text-sm text-gray-600">{getPaymentMethodIcon(subscriptionData.paymentMethod)}</div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={onChangePaymentClick}
                  className="text-blue-600 p-0 h-auto mt-2"
                >
                  Alterar forma de pagamento
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            {/* Último pagamento */}
            {subscriptionData.lastPaymentDate && (
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900">Último Pagamento</div>
                  <div className="text-sm text-gray-600">{subscriptionData.lastPaymentDate}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plano Atual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Seu Plano Atual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 border border-purple-200">
              <div className="text-4xl font-bold text-purple-900 mb-2">
                {subscriptionData.planName}
              </div>
              <div className="text-2xl font-semibold text-purple-700">
                R$ {subscriptionData.planPrice.toFixed(2).replace('.', ',')}
                <span className="text-sm text-purple-600 font-normal ml-2">/ {subscriptionData.planPeriod}</span>
              </div>
            </div>

            <Button
              onClick={onUpgradePlanClick}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold h-11"
            >
              <ArrowRight className="w-5 h-5 mr-2" />
              Visualizar Planos Superiores
            </Button>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-2" />
            Ir para Dashboard
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCancelConfirm(true)}
            className="border-red-300 text-red-700 hover:bg-red-50"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Cancelar Assinatura
          </Button>
        </div>

        {/* Confirmação de Cancelamento */}
        {showCancelConfirm && (
          <Alert className="border-red-300 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 ml-2">
              <div className="font-semibold mb-2">Tem certeza que deseja cancelar?</div>
              <p className="text-sm mb-3">
                Você perderá acesso a todos os recursos premium e aos seus dados será mantido por 30 dias antes de serem deletados permanentemente.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCancelConfirm(false)}
                  className="text-gray-700 border-gray-300 hover:bg-gray-100"
                >
                  Manter Assinatura
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    // Redirecionar para WhatsApp com mensagem pré-escrita
                    const phoneNumber = "5561982321534";
                    const message = "Olá, desejo cancelar ou alterar minha assinatura do Ateliê Pro.";
                    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, "_blank");
                    setShowCancelConfirm(false);
                  }}
                >
                  Cancelar Agora
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Benefícios */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">✨ Aproveite Todos os Benefícios</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Emissão de Notas Fiscais (NFe e NFCe)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Gestão Completa de Clientes
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Controle Avançado de Estoque
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Suporte Prioritário 24/7
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                Backups Premium Automáticos
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Help */}
        <div className="text-center text-sm text-gray-600 pt-4">
          <p>
            Precisa de ajuda?{" "}
            <button
              onClick={() => {
                const phoneNumber = "5561982321534";
                const message = "Olá, preciso de ajuda com minha assinatura do Ateliê Pro.";
                const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, "_blank");
              }}
              className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
            >
              Contate nosso suporte
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
